import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";

import { COLORS } from "../../../constants";
import {
  roleAdminService,
  type RoleAdminListItem,
} from "../../../services/roleAdminService";

type Props = {
  role: RoleAdminListItem | null;
  onClose: () => void;
};

const RoleUsersModal: React.FC<Props> = ({ role, onClose }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin", "roles", role?.id, "usuarios"],
    queryFn: () => roleAdminService.listUsuarios(role!.id),
    enabled: role != null,
  });

  useEffect(() => {
    if (!role) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [role, onClose]);

  if (!role) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Fechar"
        onClick={onClose}
      />
      <div className="relative flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">
              Usuários vinculados
            </h2>
            <p className="mt-1 text-sm text-neutral-500">{role.nome}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading && (
            <p className="text-sm text-neutral-500">Carregando…</p>
          )}
          {isError && (
            <p className="text-sm text-red-600">
              Não foi possível carregar os usuários.
            </p>
          )}
          {!isLoading && !isError && data?.length === 0 && (
            <p className="py-8 text-center text-sm text-neutral-500">
              Nenhum usuário com este perfil.
            </p>
          )}
          {!isLoading && !isError && data && data.length > 0 && (
            <ul className="divide-y divide-neutral-100">
              {data.map((u) => (
                <li key={u.id} className="flex items-center justify-between py-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-neutral-900">
                      {u.nome}
                    </p>
                    <p className="truncate text-sm text-neutral-500">
                      {u.email}
                    </p>
                    <p className="text-xs text-neutral-400">{u.lojaNome}</p>
                  </div>
                  <span
                    className={`ml-3 shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                      u.ativo
                        ? "bg-green-50 text-green-700"
                        : "bg-neutral-100 text-neutral-500"
                    }`}
                  >
                    {u.ativo ? "Ativo" : "Inativo"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between border-t px-6 py-4">
          <Link
            to="/usuarios"
            className="text-sm font-medium text-neutral-600 hover:text-neutral-900"
            onClick={onClose}
          >
            Ir para usuários
          </Link>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-semibold text-white"
            style={{ backgroundColor: COLORS.PRIMARY }}
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoleUsersModal;
